import React, { useEffect } from 'react';

import { programTypes } from '../../../../helper';
import { useProgramForm } from '../../../../context/ProgramFormContext';
import SectionCard, { SubGroup } from '../ui/SectionCard';
import Field from '../ui/Field';
import { TextInput, SelectInput, RichText } from '../ui/Inputs';
import { gray, ink, radius } from '../theme';

/**
 * Section 1 — Description.
 *
 * Same fields as `AddProgramModal.js:1604-1748`, including the read-only Welcome/Packing
 * List link that only appears once a PDF exists (there has never been an upload control).
 */
const Description = props => {
    const {
        userInput,
        setUserInput,
        setError,
        setSelectedSubCat,
        selectedSubCat,
        counselorList,
        handleChange,
        handleSelect,
        handleMultiSelect,
        handleEditerChange,
        fieldStatus,
        requiredOnly,
    } = useProgramForm();

    const categories = props.category || [];
    const subcategories = props.subcategories || [];

    // "All" (0) is not a meaningful program type — it means "unset" and only ever affected
    // search filtering, never price. It is dropped from the list but kept for a program
    // already carrying it, so editing one of those does not present a blank required field.
    const programTypeOpt = programTypes.filter(
        item => item.value !== 0 || userInput.programType === 0
    );

    // Editing an existing program hydrates `category` straight into state without going
    // through `handleCategory`, so the sub-category options were never built and the
    // dropdown opened empty until the director re-picked a category — the selected values
    // showed, but there was nothing to change them to. This covers that path, and also the
    // case where `preList` lands after hydration. The length guard keeps it out of the way
    // of interactive changes, which still run through `handleCategory` below.
    useEffect(() => {
        if (!userInput.category || (selectedSubCat || []).length) return;
        const match = categories.find(el => el._id === userInput.category);
        // Assigning the redux array by reference lets React bail out of the re-render
        // rather than looping on a fresh `[]` every pass.
        if (match && match.subCategory) setSelectedSubCat(match.subCategory);
    }, [userInput.category, categories, selectedSubCat, setSelectedSubCat]);

    // Picking a category resets the sub categories and reloads their options
    // (`AddProgramModal.js:656-662`).
    const handleCategory = e => {
        setUserInput({ category: e.value, subCategory: [] });
        const match = categories.filter(el => el._id === e.value)[0];
        setSelectedSubCat(match ? match.subCategory : []);
        setError({ category: '' });
    };

    return (
        <SectionCard
            id="description"
            step={1}
            title="Description"
            description="Identify the program and write the copy families will read"
        >
            <SubGroup title="Program basics">
                <div className="row">
                    <Field
                        className="col-md-6 mb-3"
                        label="Camp Program"
                        htmlFor="programName"
                        required
                        {...fieldStatus('programName')}
                    >
                        <TextInput
                            id="programName"
                            name="programName"
                            value={userInput.programName}
                            change={handleChange}
                            pHolder="Camp program name"
                            error={fieldStatus('programName').error}
                        />
                    </Field>

                    {!requiredOnly ? (
                        <Field
                            className="col-md-6 mb-3"
                            label="Program Code (Auto generated)"
                            htmlFor="programCode"
                            hint="Generated automatically — used on invoices and rosters."
                        >
                            <TextInput id="programCode" value={userInput.programCode} readOnly />
                        </Field>
                    ) : ''}

                    <Field
                        className="col-md-6 mb-3"
                        label="Program Category"
                        htmlFor="category"
                        required
                        {...fieldStatus('category')}
                    >
                        <SelectInput
                            id="category"
                            pHolder="Select a category"
                            change={handleCategory}
                            value={categories
                                .map(el => el._id === userInput.category && { value: el._id, label: el.name })
                                .filter(Boolean)}
                            error={fieldStatus('category').error}
                            options={categories.map(item => ({ value: item._id, label: item.name }))}
                        />
                    </Field>

                    <Field
                        className="col-md-6 mb-3"
                        label="Program Sub Category"
                        htmlFor="subCategory"
                        required
                        hint={userInput.category ? '' : 'Pick a Program Category first.'}
                        {...fieldStatus('subCategory')}
                    >
                        <SelectInput
                            id="subCategory"
                            isMulti
                            closeMenuOnSelect={false}
                            pHolder={userInput.category ? 'Select sub category' : 'Select a category first'}
                            change={e => handleMultiSelect(e, 'subCategory')}
                            value={
                                userInput.subCategory
                                    ? subcategories
                                        .filter(el => userInput.subCategory.find(e => el._id === e))
                                        .map(i => ({ value: i._id, label: i.name }))
                                    : []
                            }
                            error={fieldStatus('subCategory').error}
                            options={selectedSubCat.map(item => ({ value: item._id, label: item.name }))}
                        />
                    </Field>

                    <Field
                        className="col-md-6 mb-3"
                        label="Program Type"
                        htmlFor="programType"
                        required
                        {...fieldStatus('programType')}
                    >
                        <SelectInput
                            id="programType"
                            pHolder="Select program type"
                            change={e => handleSelect(e, 'programType')}
                            value={programTypeOpt.filter(el => el.value === userInput.programType)}
                            error={fieldStatus('programType').error}
                            options={programTypeOpt}
                        />
                    </Field>

                    {!requiredOnly ? (
                        <Field
                            className="col-md-6 mb-3"
                            label="Personnel"
                            htmlFor="counselors"
                            hint="Assign one or more staff members."
                            {...fieldStatus('counselors')}
                        >
                            <SelectInput
                                id="counselors"
                                isMulti
                                pHolder="Select Staff personnel"
                                change={e => handleMultiSelect(e, 'counselors')}
                                value={
                                    userInput.counselors
                                        ? counselorList
                                            .filter(el => userInput.counselors.find(e => el._id === e))
                                            .map(i => ({ value: i._id, label: i.name }))
                                        : []
                                }
                                options={counselorList.map(item => ({ value: item._id, label: item.name }))}
                            />
                        </Field>
                    ) : ''}
                </div>
            </SubGroup>

            <div className="mt-4">
                <SubGroup
                    title="Program copy"
                    description="Shown on the public program page in this order."
                >
                    {/* Field order matches the order these appear on the public program page:
                        About Program, Introduction, Instructions, Important Information, then
                        the Disclaimer last. Requested by the client; it differs from the
                        prototype, which led with the Introduction. */}
                    <div className="row">
                        <Field
                            className="col-md-6 mb-3 my_editer"
                            label="About Program"
                            htmlFor="aboutProgram"
                            required
                            {...fieldStatus('aboutProgram')}
                        >
                            <RichText
                                id="aboutProgram"
                                value={userInput.aboutProgram}
                                pHolder="What happens day to day, who it's for, what campers gain"
                                change={(e, delta, source) => handleEditerChange(e, 'aboutProgram', source)}
                            />
                        </Field>

                        {!requiredOnly ? (
                            <>
                                <Field
                                    className="col-md-6 mb-3 my_editer"
                                    label="Program Introduction"
                                    htmlFor="programIntroduction"
                                    {...fieldStatus('programIntroduction')}
                                >
                                    <RichText
                                        id="programIntroduction"
                                        value={userInput.programIntroduction}
                                        pHolder="A short hook that appears above the fold"
                                        change={(e, delta, source) => handleEditerChange(e, 'programIntroduction', source)}
                                    />
                                </Field>

                                <Field
                                    className="col-md-6 mb-3 my_editer"
                                    label="Program Instructions"
                                    htmlFor="programInstruction"
                                    {...fieldStatus('programInstruction')}
                                >
                                    <RichText
                                        id="programInstruction"
                                        value={userInput.programInstruction}
                                        pHolder="What to bring, drop-off details, prep steps"
                                        change={(e, delta, source) => handleEditerChange(e, 'programInstruction', source)}
                                    />
                                </Field>

                                <Field
                                    className="col-md-6 mb-3 my_editer"
                                    label="Important Information Notes"
                                    htmlFor="impNotes"
                                    {...fieldStatus('impNotes')}
                                >
                                    <RichText
                                        id="impNotes"
                                        value={userInput.impNotes}
                                        pHolder="Anything families must not miss"
                                        change={(e, delta, source) => handleEditerChange(e, 'impNotes', source)}
                                    />
                                </Field>
                            </>
                        ) : ''}

                        <Field
                            className="col-12 mb-3 my_editer"
                            label="Disclaimer Statement"
                            htmlFor="disclaimer"
                            required
                            {...fieldStatus('disclaimer')}
                        >
                            <RichText
                                id="disclaimer"
                                value={userInput.disclaimer}
                                pHolder="Liability, refund and policy language"
                                change={(e, delta, source) => handleEditerChange(e, 'disclaimer', source)}
                            />
                        </Field>

                        {/* Read-only, and only once a PDF exists — there is no upload
                            control on this form and never has been. */}
                        {userInput.pdf ? (
                            <Field className="col-12 mb-3" label="Welcome/Packing List">
                                <div
                                    style={{
                                        border: `1px solid ${gray[300]}`,
                                        borderRadius: radius.md,
                                        padding: '10px 14px',
                                        wordBreak: 'break-all',
                                        fontSize: '13px',
                                        color: ink[700],
                                    }}
                                >
                                    <a href={userInput.pdf} target="_blank" rel="noopener noreferrer">
                                        {userInput.pdf}
                                    </a>
                                </div>
                            </Field>
                        ) : ''}
                    </div>
                </SubGroup>
            </div>
        </SectionCard>
    );
};

export default Description;
